import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppSettings } from '../app-settings';

/**
 * ReservasService - Gestiona todas las llamadas a la API de reservas.
 *
 * Flujo principal:
 * 1. Crear reserva (POST /reservations)
 * 2. Añadir líneas (POST /reservations/{id}/lines) con o sin id_seat
 * 3. Editar/eliminar líneas (PUT/DELETE /reservations/{id}/lines/{lineId})
 * 4. Procesar compra (POST /reservations/{id}/purchase) → flujo de pago actual
 */
@Injectable({
  providedIn: 'root',
})
export class ReservasService {
  // Reserva activa durante el flujo de añadir entradas
  private activeReservationId = new BehaviorSubject<number | null>(null);
  public activeReservationId$ = this.activeReservationId.asObservable();

  // Señal para refrescar las líneas del panel de reserva
  private linesRefreshSubject = new Subject<void>();
  public linesRefresh$ = this.linesRefreshSubject.asObservable();

  // Señal para despintar asientos del mapa cuando se eliminan líneas de reserva
  private seatRemovedSubject = new Subject<number[]>();
  public seatRemoved$ = this.seatRemovedSubject.asObservable();

  // Cantidad total de entradas en la reserva activa
  private reservationQuantitySubject = new BehaviorSubject<number>(0);
  public reservationQuantity$ = this.reservationQuantitySubject.asObservable();

  constructor(private http: HttpClient) {}

  // --- Gestión de reserva activa (contexto de UI) ---

  setActiveReservation(id: number | null): void {
    this.activeReservationId.next(id);
  }

  getActiveReservation(): number | null {
    return this.activeReservationId.getValue();
  }

  /** Emite una señal para que el panel de reserva recargue las líneas */
  triggerLinesRefresh(): void {
    this.linesRefreshSubject.next();
  }

  /** Emite los IDs de asientos eliminados para que el mapa los despinte */
  triggerSeatRemoved(seatIds: number[]): void {
    if (seatIds.length > 0) {
      this.seatRemovedSubject.next(seatIds);
    }
  }

  /** Actualiza la cantidad total de entradas en la reserva activa */
  setReservationQuantity(quantity: number): void {
    this.reservationQuantitySubject.next(quantity);
  }

  // --- Helpers ---

  private get idCompany(): number {
    return Number(localStorage.getItem('id_business'));
  }

  // --- API de Reservas ---

  /** Listado paginado de reservas de la empresa */
  getReservations(filters: { name?: string; status?: string; event_ids?: number[] } = {}, page: number = 1) {
    const params: any = { page };
    if (filters.name?.trim()) params['name'] = filters.name.trim();
    if (filters.status) params['status'] = filters.status;
    if (filters.event_ids?.length) params['event_ids'] = filters.event_ids.join(',');
    return this.http.get<any>(`${AppSettings.API_URI_V2}/reservations/${this.idCompany}`, { params });
  }

  /** Crear nueva reserva (id_company requerido por la API) */
  createReservation(name: string) {
    const payload: any = { name, id_company: this.idCompany };
    return this.http.post<any>(
      `${AppSettings.API_URI_V2}/reservations`,
      payload
    );
  }

  /** Obtener detalle de una reserva */
  getReservation(id: number) {
    return this.http.get<any>(`${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${id}`);
  }

  /** Actualizar nombre de la reserva */
  updateReservation(id: number, name: string) {
    return this.http.put<any>(`${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${id}`, {
      name,
    });
  }

  /** Eliminar reserva completa */
  deleteReservation(id: number) {
    return this.http.delete<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${id}`
    );
  }

  // --- API de Líneas de Reserva ---

  /**
   * Añadir línea a la reserva.
   * Para eventos con mapa: incluir id_seat.
   * Para eventos sin mapa: incluir quantity.
   */
  addLine(reservationId: number, line: any) {
    return this.http.post<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/lines`,
      line
    );
  }

  addManyLines(reservationId: number, lines: any[]) {
    return this.http.post<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/lines/bulk`,
      { lines }
    );
  }

  /** Actualizar cantidad de una línea */
  updateLine(reservationId: number, lineId: number, quantity: number) {
    return this.http.put<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/lines/${lineId}`,
      { quantity }
    );
  }

  /** Eliminar línea de la reserva */
  deleteLine(reservationId: number, lineId: number) {
    return this.http.delete<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/lines/${lineId}`
    );
  }

  /** Eliminar todas las líneas de la reserva */
  clearLines(reservationId: number) {
    return this.http.delete<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/lines`
    );
  }

  /** Convertir un carrito abierto en reserva nombrada */
  fromCart(cartId: number, name: string) {
    return this.http.post<any>(`${AppSettings.API_URI_V2}/reservations/from-cart`, {
      id_cart: cartId,
      id_company: this.idCompany,
      name,
    });
  }

  // --- Compra ---

  /**
   * Procesar la compra de la reserva.
   * Lanza el flujo de pago y genera el pedido.
   */
  purchase(reservationId: number, paymentData: any) {
    return this.http.post<any>(
      `${AppSettings.API_URI_V2}/reservations/${this.idCompany}/${reservationId}/purchase`,
      paymentData
    );
  }
}
